import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import ClientLayout from "../../components/portal/ClientLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  CreditCard,
  Clock,
  TrendingUp,
  RefreshCw,
  Calendar,
  CheckCircle,
  Pause,
  XCircle,
  Loader2,
  AlertCircle,
  DollarSign,
  ArrowRight,
  Info,
} from "lucide-react";

const ClientSubscriptions = () => {
  const { token, API } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const response = await fetch(`${API}/client/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data);
      }
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async (cancelImmediately = false) => {
    if (!selectedSub) return;
    
    setCancelling(true);
    try {
      const response = await fetch(
        `${API}/client/subscriptions/${selectedSub.subscription_id}/cancel?cancel_immediately=${cancelImmediately}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.ok) {
        toast.success(
          cancelImmediately 
            ? "Subscription cancelled immediately" 
            : "Subscription will be cancelled at the end of the billing period"
        );
        fetchSubscriptions();
        setCancelModalOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to cancel subscription");
      }
    } catch (error) {
      toast.error("Failed to cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { variant: "outline", icon: Clock, label: "Awaiting Payment", className: "border-yellow-500 text-yellow-600" },
      active: { variant: "default", icon: CheckCircle, label: "Active", className: "bg-green-600" },
      paused: { variant: "outline", icon: Pause, label: "Paused", className: "border-orange-500 text-orange-600" },
      cancelled: { variant: "destructive", icon: XCircle, label: "Cancelled" },
      cancelling: { variant: "outline", icon: XCircle, label: "Cancelling", className: "border-red-500 text-red-600" },
    };
    const { variant, icon: Icon, label, className } = config[status] || config.pending;
    return (
      <Badge variant={variant} className={className}>
        <Icon className="h-3 w-3 mr-1" />
        {label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getNextBillingDate = (subscription) => {
    if (!subscription.billing_cycle_start) return null;
    const start = new Date(subscription.billing_cycle_start);
    start.setMonth(start.getMonth() + 1);
    return start;
  };

  // Calculate summary stats
  const activeSubscriptions = subscriptions.filter(s => s.status === "active");
  const totalMonthlyHours = activeSubscriptions.reduce((sum, s) => sum + s.monthly_hours, 0);
  const totalHoursUsed = activeSubscriptions.reduce((sum, s) => sum + (s.hours_used_this_cycle || 0), 0);
  const totalHoursAvailable = activeSubscriptions.reduce((sum, s) => sum + (s.hours_available || s.monthly_hours), 0);
  const totalMonthlyValue = activeSubscriptions.reduce((sum, s) => sum + s.monthly_price, 0);

  return (
    <ClientLayout activeNav="subscriptions">
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-xl font-bold text-rnb-navy">My Subscriptions</h1>
            <p className="text-sm text-gray-400 mt-0.5">View your retainer subscriptions, track hours usage, and manage billing.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-rnb-teal/30 border-t-rnb-teal rounded-full animate-spin"></div>
          </div>
        ) : subscriptions.length > 0 ? (
          <>
            {/* Summary Cards */}
            <div className="grid md:grid-cols-4 gap-4 mb-8">
              <Card data-testid="summary-active">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-rnb-navy">{activeSubscriptions.length}</p>
                      <p className="text-sm text-rnb-gray">Active Retainers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="summary-hours">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Clock className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-rnb-navy">{totalHoursAvailable.toFixed(1)}</p>
                      <p className="text-sm text-rnb-gray">Hours Available</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="summary-used">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <TrendingUp className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-rnb-navy">{totalHoursUsed.toFixed(1)}</p>
                      <p className="text-sm text-rnb-gray">Hours Used This Cycle</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="summary-value">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <DollarSign className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-rnb-navy">${totalMonthlyValue.toLocaleString()}</p>
                      <p className="text-sm text-rnb-gray">Monthly Retainer</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Subscription Cards */}
            <div className="space-y-6">
              {subscriptions.map((sub) => {
                const hoursPercent = sub.monthly_hours > 0 
                  ? Math.min(100, ((sub.hours_used_this_cycle || 0) / sub.monthly_hours) * 100) 
                  : 0;
                const nextBilling = getNextBillingDate(sub);
                const isActive = sub.status === "active";
                const isPending = sub.status === "pending";
                
                return (
                  <Card 
                    key={sub.subscription_id} 
                    data-testid={`subscription-${sub.subscription_id}`}
                    className={`overflow-hidden ${!isActive && !isPending ? 'opacity-75' : ''}`}
                  >
                    {/* Header with colored bar */}
                    <div className={`h-2 ${isActive ? 'bg-green-500' : isPending ? 'bg-yellow-500' : 'bg-slate-300'}`} />
                    
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                        {/* Left: Subscription Info */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="p-3 bg-green-100 rounded-xl">
                                <CreditCard className="h-6 w-6 text-green-600" />
                              </div>
                              <div>
                                <h3 className="text-xl font-semibold text-rnb-navy">
                                  ${sub.monthly_price?.toLocaleString()}/month
                                </h3>
                                <p className="text-rnb-gray">
                                  {sub.monthly_hours} consulting hours
                                </p>
                              </div>
                            </div>
                            {getStatusBadge(sub.status)}
                          </div>

                          {/* Hours Progress */}
                          {isActive && (
                            <div className="space-y-2 mb-4">
                              <div className="flex justify-between text-sm">
                                <span className="text-rnb-gray">Hours This Cycle</span>
                                <span className="font-medium text-rnb-navy">
                                  {(sub.hours_used_this_cycle || 0).toFixed(1)} / {sub.monthly_hours} hrs
                                </span>
                              </div>
                              <Progress 
                                value={hoursPercent} 
                                className={`h-3 ${hoursPercent > 80 ? '[&>div]:bg-orange-500' : ''}`}
                              />
                              <div className="flex justify-between text-xs text-rnb-gray">
                                <span>{hoursPercent.toFixed(0)}% used</span>
                                <span className="font-medium text-green-600">
                                  {(sub.hours_available || sub.monthly_hours - (sub.hours_used_this_cycle || 0)).toFixed(1)} hrs remaining
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Pending Payment Alert */}
                          {isPending && (
                            <div className="flex items-start gap-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200 mb-4">
                              <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="font-medium text-yellow-800">Payment Method Required</p>
                                <p className="text-sm text-yellow-700 mt-1">
                                  Please add a payment method to activate this subscription and start using your consulting hours.
                                </p>
                                <Button
                                  size="sm"
                                  className="mt-3"
                                  onClick={() => window.location.href = '/portal/payment-methods'}
                                >
                                  Add Payment Method
                                  <ArrowRight className="h-4 w-4 ml-1" />
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Rollover Info */}
                          {sub.hours_rollover && isActive && (
                            <div className="flex items-center gap-2 text-sm p-3 bg-blue-50 rounded-lg border border-blue-100">
                              <RefreshCw className="h-4 w-4 text-blue-600" />
                              <span className="text-blue-700">
                                Rollover enabled
                                {sub.rollover_balance > 0 && (
                                  <span className="font-medium"> • {sub.rollover_balance} bonus hours from last cycle</span>
                                )}
                                {sub.max_rollover_hours && (
                                  <span className="text-blue-600"> (max {sub.max_rollover_hours} hrs)</span>
                                )}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Right: Stats Grid */}
                        <div className="lg:w-72 grid grid-cols-2 gap-3">
                          <div className="p-3 bg-slate-50 rounded-lg text-center">
                            <Clock className="h-4 w-4 text-rnb-gray mx-auto mb-1" />
                            <p className="text-lg font-semibold text-rnb-navy">
                              {sub.monthly_hours}
                            </p>
                            <p className="text-xs text-rnb-gray">Monthly Hours</p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg text-center">
                            <TrendingUp className="h-4 w-4 text-rnb-gray mx-auto mb-1" />
                            <p className="text-lg font-semibold text-rnb-navy">
                              {(sub.hours_available || sub.monthly_hours).toFixed(1)}
                            </p>
                            <p className="text-xs text-rnb-gray">Available</p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg text-center">
                            <RefreshCw className="h-4 w-4 text-rnb-gray mx-auto mb-1" />
                            <p className="text-lg font-semibold text-rnb-navy">
                              {sub.rollover_balance || 0}
                            </p>
                            <p className="text-xs text-rnb-gray">Rollover Hrs</p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg text-center">
                            <Calendar className="h-4 w-4 text-rnb-gray mx-auto mb-1" />
                            <p className="text-sm font-medium text-rnb-navy">
                              {nextBilling ? formatDate(nextBilling) : "N/A"}
                            </p>
                            <p className="text-xs text-rnb-gray">Next Billing</p>
                          </div>
                        </div>
                      </div>

                      {/* Footer with actions */}
                      {isActive && (
                        <div className="flex items-center justify-between mt-6 pt-4 border-t">
                          <div className="flex items-center gap-2 text-sm text-rnb-gray">
                            <Info className="h-4 w-4" />
                            <span>Billing cycle started {formatDate(sub.billing_cycle_start)}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setSelectedSub(sub);
                              setCancelModalOpen(true);
                            }}
                          >
                            Cancel Subscription
                          </Button>
                        </div>
                      )}

                      {sub.status === "cancelling" && (
                        <div className="mt-4 pt-4 border-t">
                          <p className="text-sm text-orange-600">
                            This subscription will be cancelled at the end of the current billing period.
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <CreditCard className="h-12 w-12 text-rnb-gray/30 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-rnb-navy mb-2">No Active Subscriptions</h3>
              <p className="text-rnb-gray mb-4">
                You don't have any retainer subscriptions yet. Contact your consultant to set up a monthly retainer.
              </p>
              <Button variant="outline" onClick={() => window.location.href = '/portal/chat'}>
                Chat with Consultant
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Cancel Confirmation Modal */}
        <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <XCircle className="h-5 w-5" />
                Cancel Subscription
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel this subscription?
              </DialogDescription>
            </DialogHeader>

            {selectedSub && (
              <div className="py-4">
                <div className="p-4 bg-slate-50 rounded-lg mb-4">
                  <p className="font-semibold text-rnb-navy">
                    ${selectedSub.monthly_price?.toLocaleString()}/month
                  </p>
                  <p className="text-sm text-rnb-gray">
                    {selectedSub.monthly_hours} consulting hours
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="p-3 border rounded-lg">
                    <p className="font-medium text-rnb-navy">Cancel at end of billing period</p>
                    <p className="text-sm text-rnb-gray">
                      Keep access until {formatDate(getNextBillingDate(selectedSub))}. 
                      You won't be charged again.
                    </p>
                  </div>
                  <div className="p-3 border border-red-200 bg-red-50 rounded-lg">
                    <p className="font-medium text-red-700">Cancel immediately</p>
                    <p className="text-sm text-red-600">
                      Lose access right away. Any unused hours will be forfeited.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setCancelModalOpen(false)}
                disabled={cancelling}
              >
                Keep Subscription
              </Button>
              <Button
                variant="outline"
                className="text-orange-600 border-orange-200 hover:bg-orange-50"
                onClick={() => handleCancelSubscription(false)}
                disabled={cancelling}
              >
                {cancelling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Cancel at Period End
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleCancelSubscription(true)}
                disabled={cancelling}
              >
                {cancelling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Cancel Now
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ClientLayout>
  );
};

export default ClientSubscriptions;
